import type { ParsedUrlQuery } from 'querystring'
import { getListSpacesQueryOptions } from '@cocoonspace/sdk-js/domains/spaces/spaces'
import type { ListSpacesParams } from '@cocoonspace/sdk-js/types/listSpacesParams'
import type { Zone } from '@cocoonspace/sdk-js/types/zone'
import { defaultI18nNs } from '@cocoonspace/shared/config/default-i-18-n-ns-config'
import { searchConfig } from '@cocoonspace/shared/config/search-config'
import { QueryClient, dehydrate } from '@tanstack/react-query'
import type { GetStaticPaths, GetStaticProps } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { select } from '~/domains/search/config/select-api-keys'
import { DistrictPage } from '~/domains/spaces/components/pages/district-page'

export default DistrictPage

interface SearchPageParams extends ParsedUrlQuery {
	zone: Zone
	district: string
}

export const getStaticProps: GetStaticProps<{}, SearchPageParams> = async ({
	locale,
	params,
}) => {
	const queryClient = new QueryClient()
	const zone = (params?.zone as Zone) || 'paris'

	try {
		const district = searchConfig.districts[zone]?.find(
			({ uriParam }) => uriParam === params?.district,
		)

		const filters: ListSpacesParams = {
			zone,
			select,
			postal_code: district?.zip,
		}

		await queryClient.fetchQuery(getListSpacesQueryOptions(filters))

		return {
			props: {
				...(await serverSideTranslations(locale as string, [
					...defaultI18nNs,
					'space',
				])),
				districtZip: district?.zip,
				dehydratedState: dehydrate(queryClient),
				filters,
			},
			revalidate: 24 * 60 * 60, // 24h
		}
	} catch (e) {
		return { notFound: true }
	}
}

export const getStaticPaths: GetStaticPaths = () => {
	const paris = searchConfig.districts.paris.map((district) => ({
		zone: 'paris',
		district: district.uriParam,
	}))

	const lyon = searchConfig.districts.lyon.map((district) => ({
		zone: 'lyon',
		district: district.uriParam,
	}))

	return {
		paths: [...paris, ...lyon].map((params) => ({ params })),
		fallback: false,
	}
}
